// Every status word this platform puts in front of a customer, with what it
// means and whether it wants acting on.
//
// WHY ONE FILE. These five vocabularies are read on three screens and they are
// routinely confused with each other — `NO_ACTIONS` (our pipeline had nothing
// configured to run) and `NO_CONVERSATION` (nobody spoke) are different answers
// to different questions, and a screen that defines one of them where it
// happens to be rendered will eventually define it differently somewhere else.
//
// WHY EVERY ROW CARRIES ITS MEANING. A status explained without its number is a
// glossary; a number without its meaning is a puzzle. The Lead Overview screen
// shows both on one row, which is only possible because the words live beside
// the keys rather than in prose somewhere else on the page.
//
// WHY `tone` IS NOT SEVERITY. It says what the status MEANS, not how big it is.
// NOT_CONFIGURED and LOCAL_ONLY are settings and stay neutral however many rows
// sit in them — painting them amber tells a business running correctly that
// something is broken, every single time an agent does its job.
import type { Tone } from './leadGist';

export interface StatusMeta {
  key: string;
  label: string;
  /** One sentence: what this status actually says about the row. */
  meaning: string;
  /** What to do about it, when there is something to do. */
  advice?: string;
  tone: Tone;
  /** True when a person has to do something. Never true for a setting. */
  attention?: boolean;
}

export interface StatusFamily {
  id: string;
  title: string;
  /** What question this whole family answers — the thing that keeps them apart. */
  subtitle: string;
  /** 'leads' or 'calls' — which total the counts add up to. */
  counts: 'leads' | 'calls';
  statuses: StatusMeta[];
}

/** How a call ended, read from the transcript. One per call. */
const OUTCOME: StatusFamily = {
  id: 'outcome',
  title: 'How the call ended',
  subtitle: 'One per call, set from the transcript when it finishes.',
  counts: 'calls',
  statuses: [
    {
      key: 'NO_CONVERSATION', label: 'Nothing was said', tone: 'bad', attention: true,
      meaning: 'The call connected and no words were transcribed at either end — not even our greeting.',
      advice: 'Worth chasing if the call was long: an empty transcript on a long call is a lost recording, not a quiet caller.',
    },
    {
      key: 'GREETING_ONLY', label: 'Only our greeting', tone: 'bad', attention: true,
      meaning: 'The agent spoke and the caller never answered back.',
      advice: 'Usually the greeting runs long, or audio starts after the caller does.',
    },
    {
      key: 'CONVERSATION', label: 'A conversation', tone: 'neutral',
      meaning: 'They talked to us. Nothing has been extracted from it yet — a resting state, not a result.',
    },
    {
      key: 'LEAD_RECOVERED', label: 'A lead came out of it', tone: 'good',
      meaning: 'We read the transcript afterwards and built a lead from it.',
    },
    {
      key: 'NO_LEAD_FOUND', label: 'No lead in it', tone: 'warn',
      meaning: 'A real conversation that contained no lead — a wrong number, a complaint, an existing customer.',
      advice: 'If these clearly were leads, the agent is not being asked for the right fields.',
    },
    {
      key: 'Not classified', label: 'Not classified', tone: 'neutral',
      meaning: 'Unmeasured, not empty. The call ran before we started recording how calls end.',
    },
  ],
};

/** Where the lead's CRM copy got to. One per lead. */
const SYNC: StatusFamily = {
  id: 'sync',
  title: 'Where the lead got to',
  subtitle: 'One per lead. Two of these are settings, not problems.',
  counts: 'leads',
  statuses: [
    {
      key: 'SYNCED', label: 'In the CRM', tone: 'good',
      meaning: 'Written into the connected CRM. The lead exists in both places.',
    },
    {
      key: 'QUEUED', label: 'Retrying', tone: 'warn', attention: true,
      meaning: 'The push failed and a retry is scheduled. It flips to synced by itself if the retry lands.',
      advice: 'If it has not cleared within the hour, it is usually the CRM credentials.',
    },
    {
      key: 'FAILED', label: 'Not in the CRM', tone: 'bad', attention: true,
      meaning: 'The push failed and will not be retried. The lead is here and nowhere else.',
      advice: 'Chase every one.',
    },
    {
      key: 'INCOMPLETE', label: 'Missing required fields', tone: 'warn', attention: true,
      meaning: 'Held back: the lead does not carry a field this agent declares mandatory, so nothing was offered to the CRM.',
      advice: 'The only status here about the lead rather than the integration. Complete it, or change what the agent requires. Nothing retries it — a retry cannot supply what the caller never said.',
    },
    {
      key: 'LOCAL_ONLY', label: 'Kept here by choice', tone: 'neutral',
      meaning: 'A CRM is connected and this agent is deliberately configured not to push to it.',
    },
    {
      key: 'NOT_CONFIGURED', label: 'No CRM connected', tone: 'neutral',
      meaning: 'There is nothing to push to. These leads live in cpass.',
    },
  ],
};

/** Which route the lead arrived by. cpass writes this; no agent payload can reach it. */
const ORIGIN: StatusFamily = {
  id: 'origin',
  title: 'Where the lead came from',
  subtitle: 'One per lead. cpass writes this — no agent payload can set it.',
  counts: 'leads',
  statuses: [
    {
      key: 'IN_CALL', label: 'Captured on the call', tone: 'good',
      meaning: 'The caller was on the line and the agent took the details down.',
    },
    {
      key: 'POST_CALL', label: 'Recovered afterwards', tone: 'neutral',
      meaning: 'Rebuilt from the transcript after the call, because no lead was captured live.',
      advice: 'Not a failure — a lead that would otherwise have been lost. These are the ones that arrive without a name.',
    },
    {
      key: 'WEB', label: 'From a web form', tone: 'ok',
      meaning: 'Somebody filled in a form on a website. There was no call at all.',
      advice: 'These have no outcome, transcript or recording, so they are absent from every call figure on this screen — by design, not by omission.',
    },
  ],
};

/** What our own post-call processing did. A different question from the outcome. */
const PIPELINE: StatusFamily = {
  id: 'pipeline',
  title: 'What the pipeline did',
  subtitle: 'Whether our processing ran — not how the call went.',
  counts: 'calls',
  statuses: [
    {
      key: 'RECEIVED', label: 'Waiting', tone: 'neutral',
      meaning: 'The call arrived and is waiting its turn. Normal for a few seconds.',
      advice: 'A backlog only if it stays high for more than a minute or two.',
    },
    {
      key: 'PROCESSED', label: 'Processed', tone: 'good',
      meaning: 'Everything configured to run after the call ran and finished.',
    },
    {
      key: 'NO_ACTIONS', label: 'Nothing to do', tone: 'neutral',
      meaning: 'Nothing was configured to run after this call. The transcript is still kept. Not a failure.',
    },
    {
      key: 'PARTIAL', label: 'Partly done', tone: 'warn', attention: true,
      meaning: 'Some post-call actions ran and at least one did not.',
      advice: 'Open the call to see which action failed.',
    },
    {
      key: 'FAILED', label: 'Failed', tone: 'bad', attention: true,
      meaning: 'Processing threw before finishing. The only status here that loses data.',
      advice: 'Chase every one — the lead may not exist at all.',
    },
  ],
};

/** What the agent judged. Only agents whose prompt asks for a rating produce one. */
const QUALITY: StatusFamily = {
  id: 'quality',
  title: 'What the agent rated the lead',
  subtitle: 'One per lead. Only agents whose prompt asks for a rating produce one.',
  counts: 'leads',
  statuses: [
    { key: 'High', label: 'High', tone: 'good', meaning: 'Clear intent, and enough detail to act on.' },
    { key: 'Medium', label: 'Medium', tone: 'neutral', meaning: 'Interested, with something missing — a date, a budget, a number.' },
    { key: 'Low', label: 'Low', tone: 'warn', meaning: 'Barely qualified. Often a very short call.' },
    {
      key: 'Not set', label: 'Not rated', tone: 'neutral',
      meaning: 'No rating was asked for. Unmeasured, not bad.',
      advice: 'Name lead_quality on the agent’s lead form if you want this to mean something.',
    },
  ],
};

/** The five, in the order the screen reads them. */
export const STATUS_FAMILIES: StatusFamily[] = [OUTCOME, SYNC, ORIGIN, PIPELINE, QUALITY];

const BY_KEY = new Map<string, StatusMeta>();
STATUS_FAMILIES.forEach((f) => f.statuses.forEach((s) => BY_KEY.set(`${f.id}:${s.key}`, s)));

/**
 * One status's meaning, or a safe stand-in for a key this file has not met.
 *
 * An unknown status renders under its own name with no alarm — the alternative
 * is a screen that either hides a bucket or shouts about one it cannot explain.
 */
export function statusMeta(familyId: string, key: string): StatusMeta {
  const known = BY_KEY.get(`${familyId}:${key}`);
  if (known) return known;
  return { key, label: key, meaning: 'A status this screen has not been taught yet.', tone: 'neutral' };
}

/**
 * The family's rows in order, each with its count.
 *
 * Keys the server sent that this file does not know are APPENDED rather than
 * dropped, so the counts on screen always add up to the total beside them.
 */
export function familyRows(
  family: StatusFamily,
  counts: Record<string, number> | undefined,
): Array<StatusMeta & { count: number }> {
  const c = counts || {};
  const rows = family.statuses.map((s) => ({ ...s, count: c[s.key] || 0 }));
  Object.keys(c)
    .filter((k) => !family.statuses.some((s) => s.key === k))
    .forEach((k) => rows.push({ ...statusMeta(family.id, k), count: c[k] }));
  return rows;
}

/** How many rows in this family a person has to do something about. */
export function attentionIn(family: StatusFamily, counts: Record<string, number> | undefined): number {
  return familyRows(family, counts)
    .filter((r) => r.attention)
    .reduce((n, r) => n + r.count, 0);
}
